import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mapSearchStateToAiSearchContext, trackSearchAlertCreatedInDataLayer } from '@pkgs/components/utils/tracking';
import { getSecondsByFrequency } from '@pkgs/components/utils/searchAlertUtils';
import { mapSearchStateToSearchListingsFilters } from '@pkgs/components/utils/mapFilterFields';
import { type TranslationKey, type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import { PageType, TemplateType } from '@pkgs/components/types/tracking';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { type TFunction } from '@pkgs/components/providers/AppContextProvider';
import { useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import { SearchAlertFrequency, useSearchAlert } from '@pkgs/components/hooks/useSearchAlert';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useCreateSearchAlertButtonClick } from '@pkgs/components/hooks/useCreateSearchAlertButtonClick';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { type ObjectTypeCategory } from '@pkgs/components/hooks/stores/search/slices/objectTypeFilterSlice';
import { Select } from '@pkgs/components/components/Select/Select';
import { useSession } from '@ev-tech/user-account-modules';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { Modal, type ModalState } from '@ev-tech/ui-core-components/Modal';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';
import { Alert, alertVariants } from '@ev-tech/ui-core-components/Alert';
import {
  type BusinessDivisionType,
  getAiSearchContext,
  getDivisionContext,
  getPageContext,
  getShopContext,
  getUserContext,
  trackSearchAlertCreationEvent,
} from '@ev-tech/snowplow-library';
import { CrossNakedIcon as CrossNaked } from '@ev-tech/search-modules-shared/Icons';
import { BusinessArea, PropertyMarketingType } from '@ev-tech/search-modules-api';

import { NameInput } from '../NameInput';

import styles from './SearchAlertModal.module.css';

type SearchAlertFormFields = {
  name: string;
  frequency: SearchAlertFrequency;
};

export type SearchAlertModalProps = {
  modalState: ModalState;
  onOpenChange?: (isOpen: boolean) => void;
};

type SearchAlertNamePlaceholderTranslationKey = Extract<SearchModuleTranslationKey, `search.searchAlert.create.name.placeholder.${string}`>;

export const SEARCH_ALERT_NAME_PLACEHOLDER_TRANSLATION_KEYS = {
  [PropertyMarketingType.rent]: {
    [BusinessArea.commercial]: 'search.searchAlert.create.name.placeholder.rent.commercial',
    [BusinessArea.residential]: 'search.searchAlert.create.name.placeholder.rent.residential',
    ['development']: 'search.searchAlert.create.name.placeholder.rent.development',
  },
  [PropertyMarketingType.sale]: {
    [BusinessArea.commercial]: 'search.searchAlert.create.name.placeholder.sale.commercial',
    [BusinessArea.residential]: 'search.searchAlert.create.name.placeholder.sale.residential',
    ['development']: 'search.searchAlert.create.name.placeholder.sale.development',
  },
} as const satisfies Record<PropertyMarketingType, Record<ObjectTypeCategory, SearchAlertNamePlaceholderTranslationKey>>;

export const SearchAlertModal = ({ modalState, onOpenChange }: SearchAlertModalProps) => {
  const {
    t,
    TransComponent,
    tracking,
    countryCode,
    options: { language, measurementSystem, currency },
  } = useAppContext();
  const { mapABTestContext } = useSearchModuleTracking();
  const { mutate: createSearchAlert, status, isPending, data } = useSearchAlert();
  const formRef = useRef<HTMLFormElement>(null);
  const isMapSearch = useSearchStore((state) => Boolean(state.multiPolygon));
  const [errorMessage, setErrorMessage] = useState<TranslationKey | undefined>();
  const [formValues, setFormValues] = useState<SearchAlertFormFields | undefined>();
  const placeName = useSearchStore((state) => state.placeName);
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const isLipaPage = useIsLipaPage();
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const { showMessage } = useStatusMessage();
  const { isDesktop, isLaptop } = useBreakpoint();
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);
  const { session } = useSession();
  const isUserConfirmed = useMemo(() => session?.user.hasVerifiedEmailAddress, [session]);

  const propertyMarketingType = useSearchStore((state) => state.marketingType);
  const preferences = useSearchStore((state) => state.preferences);
  const searchMode = useSearchStore((state) => state.searchMode);

  const { trackButtonClickNavigationEvent, trackButtonClickUserInteractionEvent } = useCreateSearchAlertButtonClick();
  const { isOpen, setOpen } = modalState;
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      onOpenChange?.(isOpen);
    },
    [onOpenChange, setOpen],
  );
  const handleDismissSearchAlertModal = useCallback(() => handleOpenChange(false), [handleOpenChange]);

  const placeNameOrSelectedArea = useMemo(
    () => (isMapSearch ? t('search.searchAlert.filterSummary.selectedArea') : (placeName ?? '')),
    [isMapSearch, placeName, t],
  );

  useEffect(() => {
    if (!isUserConfirmed) {
      setErrorMessage('search.userAccount.confirmation.email.message');
    }
  }, [isUserConfirmed]);

  useEffect(() => {
    if (data?.status === 'success' && formValues && placeNameOrSelectedArea && session?.user.emailAddresses[0]?.emailAddress) {
      trackSearchAlertCreatedInDataLayer({
        placeName: placeNameOrSelectedArea,
        email: session.user.emailAddresses[0]?.emailAddress,
        pageType: tracking?.pageType ?? (isLipaPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT),
        templateType: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
      });

      trackSearchAlertCreationEvent({
        contexts: [
          getUserContext({ email: session.user.emailAddresses[0]?.emailAddress }),
          getShopContext({
            business_division_name: objectTypeCategory,
            id: masterDataShopIds?.[0],
          }),
          getPageContext({
            isHQPage: true,
            type: tracking?.pageType ?? (isLipaPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT),
            country_code: countryCode,
            page_language: language,
            template_type: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
          }),
          getDivisionContext({
            name: objectTypeCategory as BusinessDivisionType,
          }),
          getAiSearchContext(
            mapSearchStateToAiSearchContext({
              preferences,
              isAiSearchEnabled: searchMode === SearchMode.AI,
            }),
          ),
          mapABTestContext(isMapToggledOn),
        ],
      });
      showMessage(t('search.userAccount.searchAlert.creation.success.message'), {
        alertType: alertVariants.success,
        actionText: t('search.userAccount.searchAlert.creation.action.savedSearches'),
        onAction() {
          location.href = `/${countryCode.toLowerCase()}/${language}/account/saved-searches`;
        },
      });

      handleDismissSearchAlertModal();
    }
    if (status === 'pending') {
      trackButtonClickNavigationEvent('', `search-alert_${status}`, 'search-alert_processing');
    }
    if (data?.status === 'error') {
      handleDismissSearchAlertModal();
    }
  }, [
    status,
    placeName,
    formValues,
    data,
    masterDataShopIds,
    objectTypeCategory,
    language,
    tracking?.pageType,
    isLipaPage,
    countryCode,
    mapABTestContext,
    trackButtonClickNavigationEvent,
    showMessage,
    t,
    placeNameOrSelectedArea,
    session?.user.emailAddresses,
    preferences,
    searchMode,
    isMapToggledOn,
    handleDismissSearchAlertModal,
  ]);

  const sendFormRequest = useCallback(() => {
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const formValues = Object.fromEntries(formData.entries()) as SearchAlertFormFields;
    setFormValues(formValues);

    createSearchAlert({
      payload: {
        searchAlert: {
          searchFilter: mapSearchStateToSearchListingsFilters(getStateSnapshot()),
          currency,
          language,
          marketCountryCode: countryCode,
          measurementSystem,
          intervalSeconds: getSecondsByFrequency(formValues.frequency),
          name: formValues.name,
          placeName: placeNameOrSelectedArea,
          aiSearch: searchMode === SearchMode.AI,
        },
      },
      options: {
        language,
        currency,
        measurementSystem,
      },
    });
  }, [createSearchAlert, getStateSnapshot, currency, language, countryCode, measurementSystem, placeNameOrSelectedArea, searchMode]);

  const handleSubmitSearchAlertModal = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isPending) return;
      if (!formRef.current?.checkValidity()) {
        const firstInvalidInput = formRef.current?.querySelector<HTMLInputElement>('input:invalid');
        firstInvalidInput?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        firstInvalidInput?.focus({ preventScroll: true });
        return;
      }
      trackButtonClickNavigationEvent('search-alert_primary_submit', 'search-alert_processing', 'search-alert_modal_form');
      sendFormRequest();
    },
    [trackButtonClickNavigationEvent, sendFormRequest, isPending],
  );

  const defaultName = useMemo(() => {
    const placeholderTranslationKey = SEARCH_ALERT_NAME_PLACEHOLDER_TRANSLATION_KEYS[propertyMarketingType][objectTypeCategory];

    return t(placeholderTranslationKey, {
      placeName: isMapSearch ? t('search.searchAlert.selectedArea') : (placeName ?? ''),
      defaultValue: '',
    });
  }, [t, propertyMarketingType, objectTypeCategory, isMapSearch, placeName]);

  const handleCloseSearchAlertModal = useCallback(() => {
    handleDismissSearchAlertModal();
    trackButtonClickNavigationEvent('search-alert_tertiary_back', '', 'search-alert_modal_form');
  }, [handleDismissSearchAlertModal, trackButtonClickNavigationEvent]);

  type SearchAlertFrequencyTranslationKey = Extract<SearchModuleTranslationKey, `search.searchAlert.form.frequency.${string}`>;

  const getSearchAlertFrequencyLabel = (frequency: SearchAlertFrequency): SearchAlertFrequencyTranslationKey | undefined => {
    switch (frequency) {
      case SearchAlertFrequency.DAILY:
        return 'search.searchAlert.form.frequency.daily';
      case SearchAlertFrequency.INSTANTLY:
        return 'search.searchAlert.form.frequency.instantly';
      case SearchAlertFrequency.NEVER:
        return 'search.searchAlert.form.frequency.never';
      case SearchAlertFrequency.WEEKLY:
        return 'search.searchAlert.form.frequency.weekly';
      default:
        return undefined;
    }
  };

  return (
    <Modal modal open={isOpen} onOpenChange={handleOpenChange} data-testid="search-components_search-alert-modal">
      <form
        className={styles.modal}
        onSubmit={handleSubmitSearchAlertModal}
        ref={formRef}
        data-testid="search-components_search-alert-creation-form"
      >
        <div className={styles.topBar}>
          <IconButton
            data-testid="search-components_modal_close-button"
            icon={<CrossNaked />}
            label={t('search.common.close')}
            onClick={handleDismissSearchAlertModal}
            variant="tertiaryAlt"
          />
        </div>
        <div className={styles.modalContent}>
          {errorMessage && (
            <Alert appearance="inline" variant="warning" className={styles.alert}>
              <span>
                <TransComponent t={t as TFunction} i18nKey={errorMessage} components={{ b: <b className={styles.boldText} /> }} />
              </span>
            </Alert>
          )}
          <div className={styles.header}>
            <Headline size="h3" as="h3" label={t('search.searchAlert.create.label')} />
            <Copy size="l" text={t('search.searchAlert.create.subtitle')} />
          </div>
          <NameInput autoFocus={isDesktop || isLaptop} defaultValue={defaultName} />
          <Select
            className={styles.frequencySelect}
            onChange={() => {
              trackButtonClickUserInteractionEvent('update-frequency_search-alert');
            }}
            name="frequency"
            size="m"
            label={t('search.searchAlert.form.frequency.select.label')}
            data-testid="search-components_search-alert-creation-form_frequency-select"
            defaultValue={SearchAlertFrequency.INSTANTLY}
            accessibilityLabel={t('search.select.hiddenAccessibilityText', {
              label: t('search.searchAlert.form.frequency.select.label'),
            })}
            items={Object.values(SearchAlertFrequency).flatMap((value) => {
              const labelKey = getSearchAlertFrequencyLabel(value);
              return labelKey
                ? [
                    {
                      id: value,
                      label: t(labelKey),
                    },
                  ]
                : [];
            })}
            usesButtonWidth
            isInModal
          />
        </div>

        <div className={styles.modalFooter}>
          <Button
            variant="tertiary"
            type="button"
            label={t('search.common.cancel')}
            onClick={handleCloseSearchAlertModal}
            testId="search-components_search-alert-creation-form_cancel-button"
          />
          <Button
            type="submit"
            label={isPending ? '' : t('search.userAccount.searchAlert.modal.button.save')}
            loading={{ isLoading: isPending, label: '' }}
            testId="search-components_search-alert-creation-form_submit-button"
          />
        </div>
      </form>
    </Modal>
  );
};
